import * as i0 from '@angular/core';
import { ElementRef, Component, ChangeDetectionStrategy, Optional, Inject, Input, HostBinding, HostListener, NgModule } from '@angular/core';
import { tuiCreateToken, tuiProvideOptions, AbstractTuiInteractive, tuiIsNativeFocused, tuiAsFocusableItemAccessor, TuiDestroyService } from '@taiga-ui/cdk';
import * as i6 from '@taiga-ui/core/directives';
import { TuiModeDirective } from '@taiga-ui/core/directives';
import { EMPTY } from 'rxjs';
import { startWith, map, distinctUntilChanged } from 'rxjs/operators';
import { TuiAppearance } from '@taiga-ui/core/enums';
import * as i1 from '@taiga-ui/core/components/svg';
import { TuiSvgModule } from '@taiga-ui/core/components/svg';
import * as i2 from '@taiga-ui/core/components/loader';
import { TuiLoaderModule } from '@taiga-ui/core/components/loader';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i4 from '@taiga-ui/core/directives/wrapper';
import { TuiWrapperModule } from '@taiga-ui/core/directives/wrapper';
import * as i5 from '@tinkoff/ng-polymorpheus';
import { PolymorpheusModule } from '@tinkoff/ng-polymorpheus';
const _c0 = ["tuiButton", ""];
const _c1 = ["*"];
function TuiButtonComponent_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function TuiButtonComponent_span_4_tui_svg_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "tui-svg", 8);
  }
  if (rf & 2) {
    const text_r1 = ctx.polymorpheusOutlet;
    i0.ɵɵproperty("src", text_r1);
  }
}
function TuiButtonComponent_span_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 6);
    i0.ɵɵtemplate(1, TuiButtonComponent_span_4_tui_svg_1_Template, 1, 1, "tui-svg", 7);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("polymorpheusOutlet", ctx_r1.icon);
  }
}
function TuiButtonComponent_span_6_tui_svg_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "tui-svg", 8);
  }
  if (rf & 2) {
    const text_r3 = ctx.polymorpheusOutlet;
    i0.ɵɵproperty("src", text_r3);
  }
}
function TuiButtonComponent_span_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 9);
    i0.ɵɵtemplate(1, TuiButtonComponent_span_6_tui_svg_1_Template, 1, 1, "tui-svg", 7);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("polymorpheusOutlet", ctx_r1.iconRight);
  }
}
function TuiButtonComponent_tui_loader_7_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "tui-loader", 10);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("inheritColor", true)("size", ctx_r1.loaderSize);
  }
}
const TUI_BUTTON_DEFAULT_OPTIONS = {
  size: 'l',
  shape: null,
  appearance: TuiAppearance.Primary
};
/**
 * Default parameters for button component
 */
const TUI_BUTTON_OPTIONS = tuiCreateToken(TUI_BUTTON_DEFAULT_OPTIONS);
function tuiButtonOptionsProvider(options) {
  return tuiProvideOptions(TUI_BUTTON_OPTIONS, options, TUI_BUTTON_DEFAULT_OPTIONS);
}
let TuiButtonComponent = /*#__PURE__*/(() => {
  class TuiButtonComponent extends AbstractTuiInteractive {
    constructor(mode, el, options) {
      var _a;
      super();
      this.mode = mode;
      this.el = el;
      this.options = options;
      this.mode$ = ((_a = this.mode) === null || _a === void 0 ? void 0 : _a.change$) || EMPTY;
      this.appearance = null;
      this.disabled = false;
      this.shape = this.options.shape;
      this.showLoader = false;
      this.size = this.options.size;
      this.appearance$ = this.mode$.pipe(startWith(null), map(() => this.computedAppearance), distinctUntilChanged());
    }
    get nativeFocusableElement() {
      return this.nativeDisabled ? null : this.el.nativeElement;
    }
    get focused() {
      return !this.showLoader && tuiIsNativeFocused(this.el.nativeElement);
    }
    get loaderSize() {
      return this.size === 'l' || this.size === 'xl' ? 'm' : 's';
    }
    get computedAppearance() {
      var _a;
      return (_a = this.appearance) !== null && _a !== void 0 ? _a : this.options.appearance || '';
    }
    get nativeDisabled() {
      return this.computedDisabled || this.showLoader ? '' : null;
    }
    get tabIndex() {
      return this.focusable ? 0 : -1;
    }
    onFocused(focused) {
      this.updateFocused(focused);
    }
  }
  TuiButtonComponent.ɵfac = function TuiButtonComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TuiButtonComponent)(i0.ɵɵdirectiveInject(TuiModeDirective, 8), i0.ɵɵdirectiveInject(ElementRef), i0.ɵɵdirectiveInject(TUI_BUTTON_OPTIONS));
  };
  TuiButtonComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: TuiButtonComponent,
    selectors: [["button", "tuiButton", ""], ["button", "tuiIconButton", ""], ["a", "tuiButton", ""], ["a", "tuiIconButton", ""]],
    hostVars: 7,
    hostBindings: function TuiButtonComponent_HostBindings(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵlistener("focusin", function TuiButtonComponent_focusin_HostBindingHandler() {
          return ctx.onFocused(true);
        })("focusout", function TuiButtonComponent_focusout_HostBindingHandler() {
          return ctx.onFocused(false);
        });
      }
      if (rf & 2) {
        i0.ɵɵhostProperty("tabIndex", ctx.tabIndex);
        i0.ɵɵattribute("data-shape", ctx.shape)("data-size", ctx.size)("data-appearance", ctx.computedAppearance)("disabled", ctx.nativeDisabled);
        i0.ɵɵclassProp("_loading", ctx.showLoader);
      }
    },
    inputs: {
      appearance: "appearance",
      disabled: "disabled",
      icon: "icon",
      iconRight: "iconRight",
      shape: "shape",
      showLoader: "showLoader",
      size: "size"
    },
    standalone: false,
    features: [i0.ɵɵProvidersFeature([tuiAsFocusableItemAccessor(TuiButtonComponent), TuiDestroyService]), i0.ɵɵInheritDefinitionFeature],
    attrs: _c0,
    ngContentSelectors: _c1,
    decls: 8,
    vars: 10,
    consts: [[4, "ngIf"], ["tuiWrapper", "", 1, "t-wrapper", 3, "active", "appearance", "disabled", "hover"], [1, "t-content"], ["class", "t-left", 4, "ngIf"], ["class", "t-right", 4, "ngIf"], ["class", "t-loader", 3, "inheritColor", "size", 4, "ngIf"], [1, "t-left"], ["class", "t-icon", 3, "src", 4, "polymorpheusOutlet"], [1, "t-icon", 3, "src"], [1, "t-right"], [1, "t-loader", 3, "inheritColor", "size"]],
    template: function TuiButtonComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵtemplate(0, TuiButtonComponent_ng_container_0_Template, 1, 0, "ng-container", 0);
        i0.ɵɵpipe(1, "async");
        i0.ɵɵelementStart(2, "div", 1)(3, "span", 2);
        i0.ɵɵtemplate(4, TuiButtonComponent_span_4_Template, 2, 1, "span", 3);
        i0.ɵɵprojection(5);
        i0.ɵɵtemplate(6, TuiButtonComponent_span_6_Template, 2, 1, "span", 4);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(7, TuiButtonComponent_tui_loader_7_Template, 1, 2, "tui-loader", 5);
        i0.ɵɵelementEnd();
      }
      if (rf & 2) {
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(1, 8, ctx.appearance$));
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("active", ctx.pseudoActive)("appearance", ctx.computedAppearance)("disabled", ctx.computedDisabled)("hover", ctx.pseudoHover);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.icon);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.iconRight);
        i0.ɵɵadvance();
        i0.ɵɵproperty("ngIf", ctx.showLoader);
      }
    },
    dependencies: [i1.TuiSvgComponent, i2.TuiLoaderComponent, i3.NgIf, i4.TuiWrapperDirective, i5.PolymorpheusOutletDirective, i3.AsyncPipe],
    styles: ["[_nghost-%COMP%]{-webkit-appearance:none;-moz-appearance:none;appearance:none;padding:0;border:0;background:none;font-size:inherit;line-height:inherit;text-decoration:none;font:var(--tui-font-text-s);color:var(--tui-text-01);position:relative;display:inline-block;flex-shrink:0;vertical-align:top;border-radius:var(--tui-radius-m);-webkit-user-select:none;-moz-user-select:none;user-select:none;outline:none;color:inherit}[data-shape=rounded][_nghost-%COMP%]{border-radius:6.25rem}[data-size=xs][_nghost-%COMP%]{height:var(--tui-height-xs);font-weight:normal}[data-size=xs][data-shape=square][_nghost-%COMP%], [data-size=xs][tuiIconButton][_nghost-%COMP%]{width:var(--tui-height-xs)}[data-size=s][_nghost-%COMP%]{height:var(--tui-height-s);font-weight:normal}[data-size=s][data-shape=square][_nghost-%COMP%], [data-size=s][tuiIconButton][_nghost-%COMP%]{width:var(--tui-height-s)}[data-size=m][_nghost-%COMP%]{height:var(--tui-height-m);font:var(--tui-font-text-m);font-weight:bold}[data-size=m][data-shape=square][_nghost-%COMP%], [data-size=m][tuiIconButton][_nghost-%COMP%]{width:var(--tui-height-m)}[data-size=l][_nghost-%COMP%]{height:var(--tui-height-l);font:var(--tui-font-text-m);font-weight:bold}[data-size=l][data-shape=square][_nghost-%COMP%], [data-size=l][tuiIconButton][_nghost-%COMP%]{width:var(--tui-height-l)}[data-size=xl][_nghost-%COMP%]{height:3.75rem;font:var(--tui-font-text-m);font-weight:bold}[data-size=xl][data-shape=square][_nghost-%COMP%], [data-size=xl][tuiIconButton][_nghost-%COMP%]{width:3.75rem}[tuiIconButton][_nghost-%COMP%]{font-size:0!important}[tuiIconButton][_nghost-%COMP%]   .t-left[_ngcontent-%COMP%], [tuiIconButton][_nghost-%COMP%]   .t-right[_ngcontent-%COMP%]{margin:0!important}.t-loader[_ngcontent-%COMP%]{position:absolute;top:0;left:0;width:100%;height:100%;cursor:default}.t-left[_ngcontent-%COMP%]{margin-right:.5rem}[data-size=s][_nghost-%COMP%]   .t-left[_ngcontent-%COMP%]{margin-right:.375rem}[data-size=xs][_nghost-%COMP%]   .t-left[_ngcontent-%COMP%]{margin-right:.25rem}.t-right[_ngcontent-%COMP%]{margin:0 -.25rem 0 .25rem}[data-size=s][_nghost-%COMP%]   .t-right[_ngcontent-%COMP%]{margin:0 -.25rem 0 .375rem}[data-size=xs][_nghost-%COMP%]   .t-right[_ngcontent-%COMP%]{margin:0 -.125rem 0 .25rem}.t-icon[_ngcontent-%COMP%]{display:flex;max-width:100%;max-height:100%;align-items:center;justify-content:center}.t-left[_ngcontent-%COMP%], .t-right[_ngcontent-%COMP%]{display:flex;align-items:center;justify-content:center;width:1.5rem;height:1.5rem}[data-size=s][_nghost-%COMP%]   .t-left[_ngcontent-%COMP%], [data-size=s][_nghost-%COMP%]   .t-right[_ngcontent-%COMP%], [data-size=xs][_nghost-%COMP%]   .t-left[_ngcontent-%COMP%], [data-size=xs][_nghost-%COMP%]   .t-right[_ngcontent-%COMP%]{width:1rem;height:1rem}[tuiIconButton][data-appearance=icon][data-size=s][_nghost-%COMP%]   .t-left[_ngcontent-%COMP%], [tuiIconButton][data-appearance=icon][data-size=s][_nghost-%COMP%]   .t-right[_ngcontent-%COMP%], [tuiIconButton][data-appearance=link][data-size=s][_nghost-%COMP%]   .t-left[_ngcontent-%COMP%], [tuiIconButton][data-appearance=link][data-size=s][_nghost-%COMP%]   .t-right[_ngcontent-%COMP%]{width:1.5rem;height:1.5rem}.t-wrapper[_ngcontent-%COMP%]{box-sizing:border-box;cursor:pointer;isolation:isolate}[data-size=xs][_nghost-%COMP%]   .t-wrapper[_ngcontent-%COMP%]{padding:0 var(--tui-padding, .375rem)}[data-size=xs][_nghost-%COMP%]   .t-wrapper[_ngcontent-%COMP%]:after{border-width:1px}[data-size=s][_nghost-%COMP%]   .t-wrapper[_ngcontent-%COMP%]{padding:0 var(--tui-padding, .75rem)}[data-size=m][_nghost-%COMP%]   .t-wrapper[_ngcontent-%COMP%]{padding:0 var(--tui-padding, 1.5rem)}[data-size=l][_nghost-%COMP%]   .t-wrapper[_ngcontent-%COMP%]{padding:0 var(--tui-padding, 2.25rem)}[data-size=xl][_nghost-%COMP%]   .t-wrapper[_ngcontent-%COMP%]{padding:0 var(--tui-padding, 2.5rem)}[data-shape=square][_nghost-%COMP%]   .t-wrapper[_ngcontent-%COMP%], [tuiIconButton][_nghost-%COMP%]   .t-wrapper[_ngcontent-%COMP%]{padding:0}.t-content[_ngcontent-%COMP%]{display:flex;height:100%;align-items:center;justify-content:center;text-align:center}._loading[_nghost-%COMP%]   .t-content[_ngcontent-%COMP%]{opacity:0}"],
    changeDetection: 0
  });
  return TuiButtonComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiButtonModule = /*#__PURE__*/(() => {
  class TuiButtonModule {}
  TuiButtonModule.ɵfac = function TuiButtonModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TuiButtonModule)();
  };
  TuiButtonModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: TuiButtonModule
  });
  TuiButtonModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    imports: [[CommonModule, PolymorpheusModule, TuiWrapperModule, TuiSvgModule, TuiLoaderModule]]
  });
  return TuiButtonModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_BUTTON_DEFAULT_OPTIONS, TUI_BUTTON_OPTIONS, TuiButtonComponent, TuiButtonModule, tuiButtonOptionsProvider };
